/**
 * useInference — manages job state, result state, and polling.
 * Group E: Inference (16 state variables + 4 refs, 2 useCallbacks, 4 useEffects)
 *
 * handleAnalyseWithPrompt / handleAnalyseInternal / handleCancel / resetToIdle
 * stay in PrimateVision.tsx because they cross into useFileUpload,
 * useVideoLibrary, and usePrompts state.
 */
import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import {
  saveUserVideoResult,
  type PrimateVisionResult,
} from '../lib/api';
// PRI-496 Lane B: pollers read the PUBLIC GET /v1/analyses/{id} (status +
// progress + result in one resource; micro-cache coalesces the dual pollers).
import {
  getVisionJobStatus,
  getVisionResult,
  getJobProgress,
} from '../lib/api/public-inference';
import { playResultTone } from '../lib/sound';
import { trackAnalysisResult } from '../telemetry/events';
import { type PageState, type VideoEntry } from './types';

export interface UseInferenceReturn {
  pageState: PageState;
  pageStateRef: MutableRefObject<PageState>;
  setPageStateSync: (s: PageState) => void;
  jobId: string | null;
  setJobId: (id: string | null) => void;
  jobProgress: { stage: string; pct: number; message: string } | null;
  setJobProgress: (p: { stage: string; pct: number; message: string } | null) => void;
  resultCacheRef: MutableRefObject<Map<string, { jobId: string; result: PrimateVisionResult }>>;
  analysisGenRef: MutableRefObject<number>;
  displayedJobId: string | null;
  setDisplayedJobId: (id: string | null) => void;
  displayedResult: PrimateVisionResult | null;
  setDisplayedResult: (r: PrimateVisionResult | null) => void;
  videoKeyRef: MutableRefObject<string | null>;
  result: PrimateVisionResult | null;
  setResult: (r: PrimateVisionResult | null) => void;
  analysisErrorMessage: string | null;
  setAnalysisErrorMessage: (msg: string | null) => void;
  queuePosition: number | null;
  setQueuePosition: (pos: number | null) => void;
  videoDuration: number;
  setVideoDuration: (d: number) => void;
  processingStep: number;
  setProcessingStep: (s: number) => void;
  completingSteps: boolean;
  setCompletingSteps: (v: boolean) => void;
  revealPhase: 'hidden' | 'answer' | 'result';
  setRevealPhase: (p: 'hidden' | 'answer' | 'result') => void;
  isDemoResult: boolean;
  setIsDemoResult: (v: boolean) => void;
  startPolling: (id: string, promptForCache: string, generation?: number) => void;
  stopPolling: () => void;
  timeEstimate: number;
}

interface UseInferenceOptions {
  isSignedIn: boolean;
  isPro: boolean;
  activeVideoId: string | null;
  setVideoQueue: Dispatch<SetStateAction<VideoEntry[]>>;
  setUploadProgress: (pct: number | ((prev: number) => number)) => void;
  uploadPath: 'checking' | 'uploading' | null;
  useSam?: boolean;
}

export function useInference({
  isSignedIn,
  isPro,
  activeVideoId,
  setVideoQueue,
  setUploadProgress,
  uploadPath,
  useSam = true,
}: UseInferenceOptions): UseInferenceReturn {
  const [pageState, setPageState] = useState<PageState>('idle');
  const pageStateRef = useRef<PageState>('idle');
  const setPageStateSync = (s: PageState) => {
    pageStateRef.current = s;
    setPageState(s);
  };

  const [jobId, setJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<{ stage: string; pct: number; message: string } | null>(null);
  const resultCacheRef = useRef<Map<string, { jobId: string; result: PrimateVisionResult }>>(new Map());
  const analysisGenRef = useRef<number>(0);
  const [displayedJobId, setDisplayedJobId] = useState<string | null>(null);
  const [displayedResult, setDisplayedResult] = useState<PrimateVisionResult | null>(null);
  const videoKeyRef = useRef<string | null>(null);
  const [result, setResult] = useState<PrimateVisionResult | null>(null);
  const [analysisErrorMessage, setAnalysisErrorMessage] = useState<string | null>(null);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [processingStep, setProcessingStep] = useState(0);
  const [completingSteps, setCompletingSteps] = useState(false);
  const [revealPhase, setRevealPhase] = useState<'hidden' | 'answer' | 'result'>('hidden');
  const [isDemoResult, setIsDemoResult] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const useSamRef = useRef(useSam);
   
  useSamRef.current = useSam;

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((id: string, promptForCache: string, generation?: number) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await getVisionJobStatus(id).catch((e) => { console.warn('[poll] getVisionJobStatus error:', e); return null; });
        console.log('[poll] status:', status?.status, 'gen:', generation, 'cur:', analysisGenRef.current);
        if (!status) {
          setAnalysisErrorMessage('Job not found. It may have been deleted or expired.');
          setPageStateSync('failed');
          stopPolling();
          return;
        }
        if ((status.status as string) === 'cancelled') {
          setAnalysisErrorMessage('This analysis was cancelled.');
          setPageStateSync('failed');
          stopPolling();
          return;
        }
        if (status.status === 'complete') {
          if (generation !== undefined && analysisGenRef.current !== generation) { console.warn('[poll] stale generation, aborting'); return; }
          let res;
          try {
            res = await getVisionResult(id);
          } catch (e) {
            // Result fetch failed — keep polling, will retry on next tick
            console.warn('[poll] getVisionResult error, retrying:', e);
            return;
          }
          // Defense in depth (fix/batch-progress-stall): status='complete' is
          // expected to imply Supabase has the row, but if for any reason the
          // answer field is still missing, keep polling rather than committing
          // an empty pill. See useInference.ts progress-poll for the matching
          // guard on the other completion path.
          if (res.answer == null || res.answer === '') {
            console.warn('[poll] status=complete but answer is empty, retrying');
            return;
          }
          stopPolling();
          if (generation !== undefined && analysisGenRef.current !== generation) return;
          // Choreography: rapid-fire remaining steps to complete, then reveal result
          const renderStep = useSamRef.current ? 3 : 2;
          const completeStep = useSamRef.current ? 4 : 3;
          setCompletingSteps(true);
          setProcessingStep(renderStep);
          await new Promise(r => setTimeout(r, 80));
          setProcessingStep(completeStep);
          await new Promise(r => setTimeout(r, 400)); // Hold green flash for 400ms
          setUploadProgress(100);
          await new Promise(r => setTimeout(r, 150));
          setCompletingSteps(false);
          // Sound cue
          const isYesResult = res.answer?.toLowerCase().trim() === 'yes';
          playResultTone(isYesResult).catch(() => {});
          // Answer reveal phase
          setRevealPhase('answer');
          setResult(res);
          setDisplayedResult(res);
          setDisplayedJobId(id);
          setPageStateSync('complete');
          // Hold reveal for 800ms then show full result
          setTimeout(() => setRevealPhase('result'), 800);
          // Cache result so re-selecting this prompt is instant
          resultCacheRef.current.set(promptForCache, { jobId: id, result: res });
          // Persist result for signed-in users
          if (isSignedIn) {
            setVideoQueue(prev => {
              const active = prev.find(v => v.id === activeVideoId);
              if (active?.userVideoId) {
                void saveUserVideoResult(active.userVideoId, {
                  prompt: promptForCache,
                  job_id: id,
                  answer: res.answer ?? null,
                  confidence: res.confidence ?? null,
                  query_type: res.query_type ?? null,
                  result_json: res as unknown as Record<string, unknown>,
                }).catch(() => { /* non-fatal */ });
              }
              return prev;
            });
          }
          trackAnalysisResult({
            answer: (res.answer as 'Yes' | 'No' | null) ?? null,
            query_type: res.query_type ?? null,
            has_confidence: res.confidence != null,
            user_plan: isPro ? 'pro' : isSignedIn ? 'free' : 'demo',
          });
        } else if (status.status === 'failed') {
          stopPolling();
          setPageStateSync('failed');
        } else if (status.status === 'cancelled') {
          stopPolling();
          setPageStateSync('idle'); setAnalysisErrorMessage(null);
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, [stopPolling, isSignedIn, isPro, activeVideoId, setVideoQueue, setUploadProgress]);

  // Poll job progress during processing
  useEffect(() => {
    if (pageState !== 'processing' || !jobId) {
      setJobProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const progressRes = await getJobProgress(jobId);
        if (cancelled) return;

        if (progressRes.status === 'failed' || progressRes.status === 'cancelled') {
          cancelled = true;
          clearInterval(ivId);
          setAnalysisErrorMessage(progressRes.error_message ?? 'Analysis failed');
          setPageStateSync('failed');
          stopPolling();
          return;
        }

        // Active: check progress data
        const data = progressRes.progress;

        // stage='complete' means inference server finished — fetch result immediately
        // also trigger on status='complete' as a fallback.
        //
        // BUG (fix/batch-progress-stall, prod): The inference server's progress
        // endpoint can report stage='complete' BEFORE the Supabase row is
        // written by the async result callback. If we eagerly promote
        // pageState→'complete' with an empty getVisionResult() (answer=null),
        // the offline-pill effect renders an empty pill, the progress bars
        // unmount, and the user sees nothing.
        //
        // Guard: only commit the completion choreography when the result row
        // is genuinely populated (answer present). If the row isn't ready yet,
        // we keep the progress poller armed and re-probe on the next tick —
        // the user keeps seeing progress until Supabase has the answer.
        if (data.stage === 'complete' || progressRes.status === 'complete') {
          try {
            const r = await getVisionResult(jobId);
            const resultReady = r.answer != null && r.answer !== '';
            if (!resultReady) {
              // Race window: progress says done, result row is still empty.
              // Bail out of this tick and let the next interval re-check.
              // Do NOT clearInterval/cancel — we need to keep polling.
              return;
            }
            // Result is genuinely ready — commit completion choreography.
            cancelled = true;
            clearInterval(ivId);
            const renderStep = useSamRef.current ? 3 : 2;
            const completeStep = useSamRef.current ? 4 : 3;
            setCompletingSteps(true);
            setProcessingStep(renderStep);
            await new Promise(res => setTimeout(res, 80));
            setProcessingStep(completeStep);
            await new Promise(res => setTimeout(res, 400)); // Hold green flash for 400ms
            setCompletingSteps(false);
            setUploadProgress(100);
            const isYes = r.answer?.toLowerCase().trim() === 'yes';
            playResultTone(isYes).catch(() => {});
            setRevealPhase('answer');
            setResult(r);
            setDisplayedResult(r);
            setDisplayedJobId(jobId);
            setPageStateSync('complete');
            setTimeout(() => setRevealPhase('result'), 800);
          } catch { /* startPolling catches it as backup */ }
          return;
        }

        // 'unknown' means server hasn't started yet — preserve existing stage
        if (data.stage !== 'unknown') {
          setJobProgress(data);
        } else {
          // Unknown stage: seed with waiting message if no prior progress
          setJobProgress(prev => prev ?? { stage: 'queued', pct: 5, message: 'Waiting for inference server...' });
        }

        if (data.stage === 'preparing' || data.stage === 'predicting' || data.stage === 'segmenting') {
          setQueuePosition(null);
        }
        // When rendering, EC2 is nearly done — speed up poll to 500ms
        // so we detect completion as soon as callback lands in Supabase
        if (data.stage === 'rendering' && !cancelled) {
          clearInterval(ivId);
          ivId = setInterval(() => { void poll(); }, 500) as unknown as number;
        }
      } catch { /* ignore — startPolling is the backup completion detector */ }
    };
    void poll();
     
    let ivId = setInterval(() => { void poll(); }, 2000) as unknown as number;
    return () => { cancelled = true; clearInterval(ivId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, jobId]);

  // Map jobProgress.stage → processingStep (replaces dumb timer)
  useEffect(() => {
    if (pageState !== 'processing') {
      if (pageState !== 'failed') setProcessingStep(0);
      return;
    }
    // Before first progress or while queued/unknown, show JEPA as active (Upload done)
    if (!jobProgress || jobProgress.stage === 'queued' || jobProgress.stage === 'unknown') {
      setProcessingStep(1);
      return;
    }
    const { stage } = jobProgress;
    if (stage === 'preparing' || stage === 'parsing') {
      setProcessingStep(1);
    } else if (stage === 'predicting') {
      setProcessingStep(2);
    } else if (stage === 'segmenting') {
      setProcessingStep(useSam ? 3 : 2);
    } else if (stage === 'rendering') {
      setProcessingStep(useSam ? 3 : 2);
    }
    // 'complete' is handled by the completion choreography in the polling effects
  }, [pageState, jobProgress, useSam]);

  // Simulate upload progress for non-upload paths
  useEffect(() => {
    if (pageState === 'uploading' && uploadPath !== 'uploading') {
      setUploadProgress(0);
      const t = setInterval(() => setUploadProgress(p => Math.min(p + 15, 90)), 150);
      return () => clearInterval(t);
    } else if (pageState === 'processing') {
      setUploadProgress(100);
    }
  }, [pageState, uploadPath, setUploadProgress]);

  // Cleanup polling on unmount
  useEffect(() => () => {
    stopPolling();
  }, [stopPolling]);

  // Real timing: ~4s base + ~1.5s per second of video, cap 30s
  const timeEstimate = videoDuration > 0
    ? Math.min(Math.round(4 + videoDuration * 1.5), 30)
    : 10;

  return {
    pageState,
    pageStateRef,
    setPageStateSync,
    jobId,
    setJobId,
    jobProgress,
    setJobProgress,
    resultCacheRef,
    analysisGenRef,
    displayedJobId,
    setDisplayedJobId,
    displayedResult,
    setDisplayedResult,
    videoKeyRef,
    result,
    setResult,
    analysisErrorMessage,
    setAnalysisErrorMessage,
    queuePosition,
    setQueuePosition,
    videoDuration,
    setVideoDuration,
    processingStep,
    setProcessingStep,
    completingSteps,
    setCompletingSteps,
    revealPhase,
    setRevealPhase,
    isDemoResult,
    setIsDemoResult,
    startPolling,
    stopPolling,
    timeEstimate,
  };
}
